/**
 * Image Optimization & Lazy Loading Utilities
 * Improves Core Web Vitals (LCP, FCP, SI) without affecting design
 */

/**
 * Get optimized image props for lazy loading
 * Usage: <img {...getOptimizedImageProps(url)} />
 */
export const getOptimizedImageProps = (src: string, alt: string = '') => {
  return {
    src,
    alt,
    loading: 'lazy' as const,
    decoding: 'async' as const,
  };
};

/**
 * Convert image URL to WebP format (for Supabase CDN)
 * Improves file size by ~25-35% - CRITICAL for performance
 * Saves 393 KiB per PageSpeed Insights report
 */
export const convertToWebP = (url: string): string => {
  if (!url) return url;
  // Check if already WebP or SVG
  if (url.includes('.webp') || url.includes('.svg')) return url;

  // For Supabase URLs, append WebP format with quality
  if (url.includes('supabase')) {
    // fm=webp forces WebP conversion, q=80 balances quality/size
    return `${url}?fm=webp&q=80&auto=format`;
  }

  // For other CDN URLs, try to add WebP parameter
  if (url.includes('cloudinary')) {
    return `${url.split('?')[0]}?f_auto,q_auto:good,w_auto/dpr_auto`;
  }

  return url;
};

/**
 * Get responsive image srcset
 * Usage: <img srcSet={getResponsiveSrcSet(url)} sizes="..." />
 */
export const getResponsiveSrcSet = (url: string): string => {
  if (!url) return '';

  // For Supabase URLs
  if (url.includes('supabase')) {
    return `
      ${url}?w=320&q=75 320w,
      ${url}?w=640&q=75 640w,
      ${url}?w=1280&q=75 1280w,
      ${url}?w=1920&q=75 1920w
    `;
  }

  return url;
};

/**
 * Preload critical images (for hero/LCP images)
 * Call in useEffect on page mount
 */
export const preloadImage = (src: string): void => {
  if (typeof window === 'undefined') return;

  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  document.head.appendChild(link);
};

/**
 * Blur-up effect: Load low-quality placeholder while high-quality loads
 * Creates smooth visual transition
 */
export const createBlurHash = (url: string, quality: number = 20): string => {
  if (!url) return url;
  if (url.includes('supabase')) {
    return `${url}?w=100&q=${quality}`;
  }
  return url;
};

/**
 * Intersection Observer for lazy loading images in view
 * More efficient than loading="lazy" for controlled timing
 */
export const setupIntersectionObserver = () => {
  if (typeof window === 'undefined') return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.getAttribute('data-src');
          const srcset = img.getAttribute('data-srcset');

          if (src) {
            img.src = src;
            img.removeAttribute('data-src');
          }
          if (srcset) {
            img.srcSet = srcset;
            img.removeAttribute('data-srcset');
          }

          observer.unobserve(img);
        }
      });
    },
    { rootMargin: '50px' }
  );

  // Observe all images with data-src
  document.querySelectorAll('img[data-src]').forEach((img) => {
    observer.observe(img);
  });

  return observer;
};

/**
 * Defer non-critical CSS animations
 * Loads animations after page is interactive
 */
export const deferCriticalCSS = () => {
  if (typeof window === 'undefined') return;

  // Wait for page to be interactive
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadDeferredStyles);
  } else {
    loadDeferredStyles();
  }
};

const loadDeferredStyles = () => {
  // Find deferred stylesheets
  const deferredLinks = document.querySelectorAll('link[data-defer="true"]');
  deferredLinks.forEach((link) => {
    (link as HTMLLinkElement).rel = 'stylesheet';
    link.removeAttribute('data-defer');
  });
};

/**
 * Optimize LCP (Largest Contentful Paint)
 * Preload hero image immediately
 */
export const optimizeLCP = (heroImageUrl: string) => {
  if (!heroImageUrl) return;

  // Preload hero image
  preloadImage(heroImageUrl);

  // Set fetchpriority attribute if supported
  const heroImg = document.querySelector('img[data-lcp="true"]') as HTMLImageElement;
  if (heroImg && 'fetchPriority' in heroImg) {
    heroImg.fetchPriority = 'high';
  }
};
